import React, {memo} from 'react';

interface OtpInputProps {
    value: string;
    onChange: (value: string) => void;
    disabled?: boolean;
    error?: boolean;
    className?: string;
}

const EMPTY_VALUE = ' ';
export const OTP_LENGTH = 6;

const getSanitizedDigits = (value: string) => value.replace(/\D/g, '');

const OtpInput = ({
                      value,
                      onChange,
                      disabled = false,
                      error = false,
                      className = ''
                  }: OtpInputProps) => {
    const inputRefs = React.useRef<Array<HTMLInputElement | null>>([]);

    const normalizedValue = React.useMemo(() => {
        const current = (value ?? '').padEnd(OTP_LENGTH, EMPTY_VALUE).slice(0, OTP_LENGTH);
        return current.split('');
    }, [value]);

    const focusInput = (index: number) => {
        if (index < 0 || index >= OTP_LENGTH) {
            return;
        }
        const input = inputRefs.current[index];
        if (input) {
            input.focus();
            input.select();
        }
    }

    const updateCharactersFromIndex = (index: number, characters: string[]) => {
        const nextValue = [...normalizedValue];
        if (!characters.length) {
            nextValue[index] = EMPTY_VALUE;
        } else {
            let cursor = index;
            characters.forEach((char) => {
                if (cursor >= OTP_LENGTH) {
                    return;
                }
                nextValue[cursor] = char;
                cursor += 1;
            });
        }
        onChange(nextValue.join(''));
        if (!characters.length) {
            return index;
        }
        const lastIndex = Math.min(index + characters.length - 1, OTP_LENGTH - 1);
        return lastIndex;
    }

    const handleChange = (index: number, event: React.ChangeEvent<HTMLInputElement>) => {
        if (disabled) return;
        const digits = getSanitizedDigits(event.target.value);
        if (!digits.length) {
            updateCharactersFromIndex(index, []);
            return;
        }
        const chars = digits.split('');
        const lastIndex = updateCharactersFromIndex(index, chars);
        const nextIndex = Math.min(lastIndex + 1, OTP_LENGTH - 1);
        if (nextIndex < OTP_LENGTH) {
            focusInput(nextIndex);
        }
    }

    const handleKeyDown = (index: number, event: React.KeyboardEvent<HTMLInputElement>) => {
        if (disabled) return;
        if (event.key === 'Backspace') {
            event.preventDefault();
            if (normalizedValue[index] !== EMPTY_VALUE) {
                updateCharactersFromIndex(index, []);
                focusInput(index);
                return;
            }
            if (index > 0) {
                updateCharactersFromIndex(index - 1, []);
                focusInput(index - 1);
            }
        } else if (event.key === 'ArrowLeft') {
            event.preventDefault();
            focusInput(index - 1);
        } else if (event.key === 'ArrowRight') {
            event.preventDefault();
            focusInput(index + 1);
        }
    }

    const handlePaste = (index: number, event: React.ClipboardEvent<HTMLInputElement>) => {
        if (disabled) return;
        event.preventDefault();
        const pasted = event.clipboardData?.getData('Text') ?? '';
        const digits = getSanitizedDigits(pasted);
        if (!digits.length) {
            return;
        }
        const chars = digits.split('');
        const lastIndex = updateCharactersFromIndex(index, chars);
        const focusIndex = Math.min(lastIndex + 1, OTP_LENGTH - 1);
        focusInput(focusIndex);
    }

    const handleFocus = (index: number) => {
        const input = inputRefs.current[index];
        if (input) {
            input.select();
        }
    }

    return (
        <div className={`flex gap-2 justify-center ${className}`}>
            {normalizedValue.map((digit, index) => {
                return (
                    <input
                        key={index}
                        ref={(el) => inputRefs.current[index] = el}
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        name={`otp-${index}`}
                        autoComplete="one-time-code"
                        aria-label={`Digit ${index + 1} of ${OTP_LENGTH}`}
                        disabled={disabled}
                        maxLength={1}
                        value={digit === EMPTY_VALUE ? '' : digit}
                        onChange={(event) => handleChange(index, event)}
                        onKeyDown={(event) => handleKeyDown(index, event)}
                        onPaste={(event) => handlePaste(index, event)}
                        onFocus={() => handleFocus(index)}
                        aria-invalid={error}
                        className={`w-12 h-12 text-center text-lg border rounded-md focus:outline-none ${error ? 'border-red-500 focus:border-red-500' : 'border-gray-300 focus:border-black'} ${disabled ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-white text-black'}`}
                    />
                )
            })}
        </div>
    );
}

OtpInput.displayName = 'OtpInput';

export default memo(OtpInput);
